import { useState, useMemo, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  AgGridReact,
  Button,
  Card,
  Flex,
  Grid,
  Link,
  ModalV2,
  QueryLoader,
  Text,
  toast,
} from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import {
  ComplianceReportType,
  ComplianceCaseTypeEnum as CaseTypeEnum,
} from "~/graphql/supergraphEnums";
import {
  useDeleteComplianceDocumentMutation,
  useDownloadReportMutation,
  useGetComplianceCaseQuery,
} from "~/graphql/supergraphHooks";
import {
  DocumentActionsCellRenderParams,
  DocumentRowItem,
} from "~/pages/Compliance/CaseDetailsDrawer/Documents/types";
import {
  AGGridCellRenderParams,
  TypedAGGridCellRenderParams,
} from "~/pages/Compliance/ComplianceTable/types";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import { SPACING } from "~/styles/variables";
import { DocumentExportAction } from "../../ExportActions/utils/constants";
import { GeneratingModal } from "../../GeneratingModal";
import { isStandaloneCaseType } from "../../utils";
import { ProgramName } from "../../utils/constants";
import { DocumentActions, type DeleteDocumentInfo } from "./DocumentActions";
import { DeleteSourceDocumentModal } from "./DocumentActions/DeleteSourceDocumentModal";
import { ExpirationDateCellRenderer } from "./ExpirationDateCellRenderer";
import { ProfileCellRenderer } from "./ProfileCellRenderer";
import { ProfileRoleCellRenderer } from "./ProfileRoleCellRenderer";
import { StatusCellRenderer } from "./StatusCellRenderer";
import {
  deleteModalButton,
  deleteModalContent,
  deleteModalFooter,
  expirationDateFillCell,
  flexContainer,
  fullHeightCell,
  nameLinkLeft,
} from "./styles_vanilla.css";
import { TypeCellRenderer } from "./TypeCellRenderer";
import UploadModal from "./UploadModal";
import {
  deDuplicateDocuments,
  extractChildProfiles,
  findReadableNameByEnumValue,
  getProfileRolesByProfileId,
} from "./utils";
import { shouldShowTaxDeleteModal } from "./utils/taxDocuments";

interface Props {
  caseId: number;
  caseType: CaseTypeEnum | undefined;
  readonly: boolean;
  programName: ProgramName;
}

const Documents = ({ caseId, caseType, readonly, programName }: Props) => {
  const location = useLocation();
  const history = useHistory();
  const complianceCaseResult = useGetComplianceCaseQuery({
    variables: {
      caseId: caseId,
    },
    skip: !caseId || caseId <= 0,
  });
  const complianceCaseData = complianceCaseResult.data;
  const currentReportTypeSelected = DocumentExportAction;
  const isStandaloneCase = isStandaloneCaseType(caseType);
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType,
    complianceCaseData?.complianceCase?.entity?.complianceSettings?.fatcaCrsEnabled ?? false,
  );
  const investingProfileId = complianceCaseData?.complianceCase?.investingProfile?.id;

  const docIdsLinkedToCase: number[] =
    complianceCaseData?.complianceCase?.documents.map((document) => document.id) || [];

  const allProfiles = extractChildProfiles(complianceCaseData?.complianceCase);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [downloadReportJobId, setDownloadReportJobId] = useState(-1);
  const [createReportDownloadJob] = useDownloadReportMutation();

  const [showUpload, setShowUpload] = useState(false);

  // Delete modal state - lifted from DocumentActions to prevent AG Grid re-render issues
  const [deleteModalInfo, setDeleteModalInfo] = useState<DeleteDocumentInfo | null>(null);
  const [showSimpleDeleteModal, setShowSimpleDeleteModal] = useState(false);
  const [showTaxDeleteModal, setShowTaxDeleteModal] = useState(false);

  const [deleteComplianceDocument, { loading: deleteLoading }] =
    useDeleteComplianceDocumentMutation({
      refetchQueries: ["GetComplianceCase"],
      awaitRefetchQueries: true,
      onError: (e) => {
        toast.error(e.message);
        throw e;
      },
    });

  const isEscalationRoute = useMemo(() => {
    return location.pathname.startsWith(`${globalEnv.complianceRoute}/escalations`);
  }, [location.pathname]);

  const handleDeleteClick = useCallback(
    (info: DeleteDocumentInfo) => {
      setDeleteModalInfo(info);
      const showTaxModal = shouldShowTaxDeleteModal({
        isFatcaCrsEnabled,
        documentProfileId: info.profileId,
        investingProfileId,
        documentBaseType: info.baseType,
      });

      if (showTaxModal) {
        setShowTaxDeleteModal(true);
      } else {
        setShowSimpleDeleteModal(true);
      }
    },
    [investingProfileId, isFatcaCrsEnabled],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setShowSimpleDeleteModal(false);
    setShowTaxDeleteModal(false);
    setDeleteModalInfo(null);
  }, []);

  const handleDeleteDocument = useCallback(async () => {
    if (!deleteModalInfo) return;
    try {
      await deleteComplianceDocument({
        variables: { caseId, documentId: deleteModalInfo.documentId },
      });
      toast.success("Document deleted");
      handleCloseDeleteModal();
      const baseRoute = isEscalationRoute ? "escalations" : "case";
      history.push(`${globalEnv.complianceRoute}/${baseRoute}/${caseId}/documents`);
    } catch {
      // error handled by onError
    }
  }, [
    deleteModalInfo,
    deleteComplianceDocument,
    caseId,
    handleCloseDeleteModal,
    history,
    isEscalationRoute,
  ]);

  const documentsColDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "name",
      cellRenderer: (params: TypedAGGridCellRenderParams<DocumentRowItem>) => {
        const baseRoute = isEscalationRoute ? "escalations" : "case";
        return (
          <Flex alignItems="center" className={fullHeightCell}>
            <Link
              className={nameLinkLeft}
              to={`${globalEnv.complianceRoute}/${baseRoute}/${caseId}/document-management?documentId=${params.data.id}`}
            >
              {params.value.toString()}
            </Link>
          </Flex>
        );
      },
    },
    {
      field: "profile",
      headerName: "Profile",
      cellRenderer: (params: AGGridCellRenderParams) => (
        <ProfileCellRenderer params={params} allProfilesForCase={allProfiles} />
      ),
    },
    ...(isStandaloneCase || isFatcaCrsEnabled
      ? [
          {
            field: "profileRole",
            headerName: "Profile Role",
            cellRenderer: (params: TypedAGGridCellRenderParams<DocumentRowItem>) => (
              <ProfileRoleCellRenderer params={params} />
            ),
          },
        ]
      : []),
    {
      field: "documentPurpose",
      headerName: "Type",
      cellRenderer: (params: TypedAGGridCellRenderParams<DocumentRowItem>) => (
        <TypeCellRenderer params={params} />
      ),
    },
    ...(isStandaloneCase || isFatcaCrsEnabled
      ? [
          {
            field: "expirationDate",
            headerName: "Expiration Date",
            cellClass: expirationDateFillCell,
            cellRenderer: (params: AGGridCellRenderParams) => (
              <ExpirationDateCellRenderer value={params.value} />
            ),
          },
        ]
      : []),
    {
      field: "status",
      headerName: "Status",
      cellRenderer: (params: AGGridCellRenderParams) => <StatusCellRenderer params={params} />,
    },
    {
      maxWidth: 100,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      field: "documentActions",
      headerName: "",
      cellRenderer: (params: DocumentActionsCellRenderParams) => (
        <DocumentActions
          documentInfo={params.value}
          readonly={readonly}
          caseType={caseType}
          docIdsLinkedToCase={docIdsLinkedToCase}
          onDeleteClick={handleDeleteClick}
        />
      ),
    },
  ];
  const allRowData: DocumentRowItem[] =
    complianceCaseData?.complianceCase?.documents.map((document) => ({
      id: document.id,
      name: document.name,
      profile: document.profileId,
      profileRole: getProfileRolesByProfileId(document.profileId, allProfiles),
      documentPurpose: document.documentPurpose
        ? findReadableNameByEnumValue(document.documentPurpose.baseType)
        : "--",
      baseType: document.documentPurpose?.baseType,
      expirationDate: document.expiryDate,
      metadata: document.metadata,
      status: document.status,
      documentActions: {
        id: document?.id,
        name: document.name,
        s3Link: document.s3Link,
        profileId: document.profileId,
        baseType: document.documentPurpose?.baseType,
      },
      s3Link: document.s3Link,
    })) || [];

  const rowDataForRecurringCase: DocumentRowItem[] =
    complianceCaseData?.complianceCase?.investingProfile?.documents.map((document) => ({
      id: document.id,
      name: document.name,
      profile: document.profileId,
      documentPurpose: document.documentPurpose
        ? findReadableNameByEnumValue(document.documentPurpose.baseType)
        : "--",
      baseType: document.documentPurpose?.baseType,
      metadata: document.metadata,
      status: document.status,
      documentActions: {
        id: document?.id,
        name: document.name,
        s3Link: document.s3Link,
        profileId: document.profileId,
        baseType: document.documentPurpose?.baseType,
      },
      s3Link: document.s3Link,
    })) || [];

  const documentRowsData: DocumentRowItem[] =
    caseType === CaseTypeEnum.RecurringHit || caseType === CaseTypeEnum.Escalation
      ? deDuplicateDocuments(allRowData, rowDataForRecurringCase)
      : allRowData;

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    domLayout: "autoHeight",
    rowHeight: 61,
  };
  const onDownloadError = () => {
    toast.error(`An error occurred while generating the ${currentReportTypeSelected} report.`);
  };
  const handleExport = async () => {
    setShowGeneratingModal(true);
    const caseIds = [caseId];

    try {
      const { data } = await createReportDownloadJob({
        variables: {
          reportType: ComplianceReportType.DocumentDetails,
          caseIds: caseIds,
          idsAreSelectedVsUnselected: false,
          filters: [],
        },
      });
      if (data?.createComplianceReport?.status === "error") {
        onDownloadError();
        closeGeneratingModal();
      } else {
        setDownloadReportJobId(data?.createComplianceReport?.id as number);
      }
    } catch {
      onDownloadError();
      closeGeneratingModal();
    }
  };

  const closeGeneratingModal = () => {
    setShowGeneratingModal(false);
  };

  return (
    <QueryLoader result={complianceCaseResult}>
      <Card>
        <Flex
          css={{ marginBottom: SPACING.LARGE }}
          alignContent="center"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="secondary-md">DOCUMENTS</Text>
          <div>
            <Flex
              className={flexContainer}
              alignContent="center"
              alignItems="center"
              justifyContent="space-between"
            >
              <Button
                disabled={readonly}
                onClick={() => setShowUpload(true)}
                variant="neutral"
                icon="upload"
                data-testid="documents-upload-button"
              >
                Upload
              </Button>
              <Button
                disabled={(documentRowsData.length ?? 0) === 0}
                icon="download"
                variant="neutral"
                onClick={handleExport}
                data-testid="documents-export-button"
              >
                Export
              </Button>
            </Flex>
          </div>
        </Flex>
        <UploadModal
          showModal={showUpload}
          onClose={() => setShowUpload(false)}
          caseId={caseId}
        />
        {documentRowsData.length > 0 ? (
          <div style={{ overflow: "hidden" }}>
            <Grid
              gridOptions={gridOptions}
              rowData={documentRowsData}
              columnDefs={documentsColDefs}
              suppressColumnVirtualisation={true}
              suppressRowVirtualisation={true}
            />
          </div>
        ) : (
          <Text variant="secondary-md">No Documents</Text>
        )}
      </Card>
      {showGeneratingModal && (
        <GeneratingModal
          showGeneratingModal={showGeneratingModal}
          closeGeneratingModal={closeGeneratingModal}
          jobId={downloadReportJobId}
          reportType={currentReportTypeSelected}
        />
      )}
      {showSimpleDeleteModal && deleteModalInfo && (
        <ModalV2
          show={true}
          onHide={handleCloseDeleteModal}
          isCentered={true}
          backdrop={true}
          title="Delete document"
        >
          <Flex flexDirection="column" className={deleteModalContent}>
            <Text variant="secondary-md" marginBottom="md">
              {deleteModalInfo.documentName}
            </Text>
            <Text>
              Are you sure you want to delete this document? Doing so could impact in-progress
              or completed {programName} activities.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" className={deleteModalFooter}>
            <Button
              onClick={handleCloseDeleteModal}
              variant="neutral"
              className={deleteModalButton}
            >
              Cancel
            </Button>
            <Button
              isLoading={deleteLoading}
              onClick={() => void handleDeleteDocument()}
              variant="danger"
              className={deleteModalButton}
              data-testid="confirm-delete-document-button"
            >
              Delete document
            </Button>
          </Flex>
        </ModalV2>
      )}
      {showTaxDeleteModal &&
        deleteModalInfo &&
        deleteModalInfo.profileId !== null &&
        deleteModalInfo.baseType && (
          <DeleteSourceDocumentModal
            documentId={deleteModalInfo.documentId}
            documentName={deleteModalInfo.documentName}
            documentType={deleteModalInfo.baseType}
            profileId={deleteModalInfo.profileId}
            onClose={handleCloseDeleteModal}
            onDelete={() => void handleDeleteDocument()}
            isDeleting={deleteLoading}
          />
        )}
    </QueryLoader>
  );
};

export default Documents;
